/**
 * Additional Document Upload for Step-Up verification.
 *
 * Presents a secondary upload interface for applicants who are required to
 * provide extra documentation as part of a step-up verification flow.
 * Wraps the existing FileUpload component with step-up-specific UX.
 *
 * @module components/AdditionalDocUpload
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUpload } from './FileUpload';

export interface AdditionalDocUploadProps {
  /** Case ID the document will be associated with. */
  caseId: string;
  /** Callback when the upload is confirmed. */
  onUploadComplete: (file: File) => void;
  /** Whether the upload is currently in progress. */
  uploading?: boolean;
  /** Optional external error to display. */
  error?: string | null;
  /** Whether the component is disabled. */
  disabled?: boolean;
}

export function AdditionalDocUpload({
  caseId,
  onUploadComplete,
  uploading = false,
  error: externalError,
  disabled = false,
}: AdditionalDocUploadProps) {
  const { t } = useTranslation('portal');
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFileChange = useCallback((f: File | null) => {
    setFile(f);
    setLocalError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!file) {
      setLocalError(t('additionalDocUpload.selectFile'));
      return;
    }
    onUploadComplete(file);
  }, [file, onUploadComplete, t]);

  const displayError = externalError ?? localError;

  return (
    <div className="rounded-lg border border-hairline bg-white p-6">
      <h3 className="mb-1 text-lg font-semibold text-ink">
        {t('additionalDocUpload.title')}
      </h3>
      <p className="mb-4 text-sm text-ink-muted">
        {t('additionalDocUpload.description')}
      </p>

      <FileUpload
        id={`step-up-doc-${caseId}`}
        label={t('additionalDocUpload.uploadLabel')}
        onFileChange={handleFileChange}
        file={file}
        error={displayError}
        disabled={disabled || uploading}
      />

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!file || uploading || disabled}
          className="inline-flex items-center gap-2 rounded-md px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: 'var(--nv-primary-color)',
            '--tw-ring-color': 'var(--nv-primary-color)',
          } as React.CSSProperties}
        >
          {uploading && (
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              aria-hidden="true"
            />
          )}
          {uploading ? t('additionalDocUpload.uploading') : t('additionalDocUpload.uploadButton')}
        </button>
      </div>
    </div>
  );
}
